import {
	BookOpen,
	BookText,
	Castle,
	Layers,
	ScrollText,
	Shield,
	Users,
} from 'lucide-react'
import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	NavLink,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'

import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { MobileTabBar } from './components/mobile-tab-bar.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SkillSheetProvider } from './components/skills/skill-sheet.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, userHasRole } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=MedievalSharp&display=swap',
		},
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap',
		},
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: data
				? 'Énarques — Le Monastère'
				: 'Error | Énarques — Le Monastère',
		},
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const dbUser = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
							characters: {
								where: { mainCharacter: true, groupId: { not: null } },
								select: { id: true },
								take: 1,
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	const isGroupMember = (dbUser?.characters.length ?? 0) > 0
	const user = dbUser ? (({ characters, ...rest }) => rest)(dbUser) : null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			isGroupMember,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang='en' className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet='utf-8' />
				<meta name='viewport' content='width=device-width,initial-scale=1' />
				{allowIndexing ? null : (
					<meta name='robots' content='noindex, nofollow' />
				)}
				<Links />
			</head>
			<body className='bg-background text-foreground'>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const userIsAdmin = user ? userHasRole(user, 'admin') : false
	const theme = useTheme()
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint='/resources/images'
			getSrc={getImgSrc}
		>
			<div className='flex min-h-screen flex-col'>
				<div className='from-primary via-purple to-teal h-1.5 bg-gradient-to-r'></div>

				<header className='border-border bg-card sticky top-0 z-30 border-b backdrop-blur-sm'>
					<div className='container py-3'>
						<div className='flex items-center justify-between gap-4'>
							<Link
								to='/'
								className="flex items-center gap-3 font-['EB_Garamond',ui-serif,Georgia] text-2xl tracking-wide transition-opacity hover:opacity-90"
							>
								<img src='/img/logo-enarque.svg' alt='' className='h-16 w-16' />
								Le Monastère
							</Link>

							<div className='flex items-center gap-4'>
								{user ? (
									<UserDropdown />
								) : (
									<Button asChild variant='default' size='lg'>
										<Link to='/login'>Se connecter</Link>
									</Button>
								)}
							</div>
						</div>
					</div>
					{user ? (
						<>
							<div className='border-primary/40 bg-primary h-3 border-t xl:hidden' />
							<div className='border-primary/40 bg-primary hidden border-t xl:block'>
								<div className='container'>
									<nav className='subnav mx-auto flex flex-wrap items-center gap-3 py-2 text-sm'>
										<NavItem to='/'>
											<Castle className='h-4 w-4' /> Hub
										</NavItem>
										<NavItem to='/character' end={false}>
											<Users className='h-4 w-4' /> Profil
										</NavItem>
										<NavItem to='/group' end={false}>
											<Layers className='h-4 w-4' /> Groupe
										</NavItem>
										{userIsAdmin && (
											<NavItem to='/geopolitics' end={false}>
												<Layers className='h-4 w-4' /> Géopolitique
											</NavItem>
										)}
										<NavItem to='/content' end={false}>
											<BookText className='h-4 w-4' /> Bibliothèque
										</NavItem>
										<NavItem to='/competences' end={false}>
											<BookOpen className='h-4 w-4' /> Compétences
										</NavItem>
										{data.isGroupMember && (
											<NavItem to='/projects'>
												<ScrollText className='h-4 w-4' /> Projets
											</NavItem>
										)}
										{userIsAdmin && (
											<NavItem to='/admin'>
												<Shield className='h-4 w-4' /> Admin
											</NavItem>
										)}
									</nav>
								</div>
							</div>
						</>
					) : (
						<div className='border-primary/40 bg-primary border-t py-1.5' />
					)}
				</header>

				<div
					className={`light-content flex flex-1 flex-col ${user ? 'pb-[calc(3.5rem+env(safe-area-inset-bottom))] xl:pb-0' : ''}`}
				>
					<SkillSheetProvider>
						<Outlet />
					</SkillSheetProvider>
				</div>

				<MobileTabBar />
			</div>
			<EpicToaster closeButton position='top-center' theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function NavItem({
	to,
	end = true,
	children,
}: {
	to: string
	end?: boolean
	children: React.ReactNode
}) {
	return (
		<NavLink
			to={to}
			end={end}
			prefetch='intent'
			className={({ isActive }) =>
				`text-primary-foreground/80 inline-flex items-center gap-2 rounded-md px-3 py-1.5 transition ${
					isActive
						? 'bg-primary-foreground/15 text-primary-foreground ring-primary-foreground/25 ring-1'
						: 'hover:bg-primary-foreground/10 hover:text-primary-foreground'
				}`
			}
		>
			{children}
		</NavLink>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
